/* Copyright (c) 2017 Backpack Health LLC */
import React from 'react';
import { FormattedMessage } from 'react-intl';

import Card, { CardSection } from '../../../../components/card/Card';
import { ProfileThemeMarkdown } from '../../../../components/Markdown';
import Spacer from '../../../../components/Spacer';
import { TextLink } from '../../../../components/TextLink';
import LegalDocumentLinks from '../../../../components/LegalDocumentLinks';
import PolicyText from '../../../../components/PolicyText.connected';
import { withIntl } from '../../../../../utilities/localizedUtil';

import { ConsentInterface, ConsentPolicyState } from '../../../../../models/ConsentModel';
import { fixedMediumDate } from '../../../../localization/LocalizedDate';
import ProfileModel from '../../../../../models/ProfileModel';
import { PolicyName } from '../../../../../models/PolicyModel';
import RouteNames from '../../../RouteNames';
import { navigate } from '../../../redux/NavigationActions';

interface Props {
  intl: any;
  onWithdrawConsent?(): void;
  title?: string;
  summary?: string;
  name?: string;
  consentData: ConsentInterface;
  showSummary: boolean;
  children: any;
  privacyLink: any;
}

@withIntl
class PolicyStatusBasedOnPolicyName extends React.Component<Props, {}> {
  render() {
    const { name, intl, showSummary, children } = this.props;
    const languageCode = intl.locale;

    return (
      <PolicyText name={name} languageCode={languageCode}>
        {
          ({ title, summary }) => (
            <PolicyStatus title={title} summary={showSummary ? summary : ''} {...this.props} />
          )
        }
      </PolicyText>
    );
  }
}

const ConsentStatusMessage = (props) => {
  const { consentData, acceptedMessageId, withdrawMessageId, notProvidedMessageId } = props;
  const consentState = consentData ? consentData.state : '';

  switch (consentState) {
    case ConsentPolicyState.provided:
      return (
        <Spacer size="14" topOnly={true}>
          <Accepted consentData={consentData} phraseId={acceptedMessageId} />
        </Spacer>
      );
    case ConsentPolicyState.withheld:
      return (
        <Spacer size="14" topOnly={true}>
          <Accepted consentData={consentData} phraseId={withdrawMessageId} />
        </Spacer>
      );
    case ConsentPolicyState.notConsented:
    default:
      return notProvidedMessageId && (
        <Spacer size="14" topOnly={true}>
          <FormattedMessage id={notProvidedMessageId} />
        </Spacer>
      );
  }
};

const ConsentAction = (props) => {
  const { allowAction, consentData, onWithdrawConsent, onProvideConsent, withdrawMessageId, providePhraseId } = props;
  const consentState = consentData ? consentData.state : '';

  if (!allowAction) { return null; }

  switch (consentState) {
    case ConsentPolicyState.provided:
      return withdrawMessageId && (
        <Spacer size="24" topOnly={true}>
          <TextLink className="font-subhead font-color-gsDark" style={{ display: 'block', textDecoration: 'underline' }} onClick={onWithdrawConsent}>
            <FormattedMessage id={withdrawMessageId} />
          </TextLink>
        </Spacer>
      );
    case ConsentPolicyState.withheld:
    case ConsentPolicyState.notConsented:
      return providePhraseId && (
        <Spacer size="24" topOnly={true}>
          <TextLink className="font-subhead font-color-gsDark" style={{ display: 'block', textDecoration: 'underline' }} onClick={onProvideConsent}>
            <FormattedMessage id={providePhraseId} />
          </TextLink>
        </Spacer>
      );
    default:
      return null;
  }
}

class PolicyStatus extends React.Component<Props, {}> {

  render() {
    const { title, summary, privacyLink } = this.props;

    return (
      <Card className="bkgd-color-gsLightest" hasDropshadow={true}>
        <CardSection hasBorder={true}>
          <div className="font-headline font-color-gsDark">
            {title}
          </div>

          {
            summary && (
              <Spacer size="14" topOnly={true}>
                <ProfileThemeMarkdown text={summary} />
              </Spacer>
            )
          }

          {
            this.props.children
          }
        </CardSection>

        <CardSection hasBorder={false}>
          {privacyLink}
        </CardSection>
      </Card>
    );
  }
}

interface AcceptedProps {
  consentData: ConsentInterface;
  phraseId: string;
}

const Accepted = (props: AcceptedProps) => {
  const { consentData, phraseId } = props;
  const profile = new ProfileModel(consentData.consenting_profile);

  return (
    <div>
      <div className="font-body font-color-gsDarkest" style={{ marginBottom: 4 }}>
        { phraseId && <FormattedMessage id={phraseId} values={{ string1: profile.formalName() }} />}
      </div>
      <div className="font-footnote font-color-gsMiddle">
        <FormattedMessage
          id="privacy.informedConsent.dateVersion.label"
          values={{
            string1: fixedMediumDate(consentData.consent_date, 'P1D', null),
            string2: consentData.consent_policy_version,
          }}
        />
      </div>
    </div>
  );
};

const HealthInfoPolicyStatus = (props: any) => {
  const { consentData, onWithdrawConsent, allowWithdraw } = props;

  return (
    <PolicyStatusBasedOnPolicyName
      {...props}
      title={<FormattedMessage id="privacy.healthInfoBlock.heading" />}
      name={PolicyName.HEALTH_INFO}
      showSummary={true}
      privacyLink={(
        <LegalDocumentLinks.PrivacyPolicy className="font-footnote font-color-profile-accent">
          <FormattedMessage id="privacy.seeFullPrivacyPolicy.button.label" />
        </LegalDocumentLinks.PrivacyPolicy>
      )}
    >
      <ConsentStatusMessage
        consentData={consentData}
        acceptedMessageId="privacy.healthInfo.notUserProvided.message"
        withdrawMessageId=""
        notProvidedMessageId=""
      />

      <ConsentAction
        allowAction={allowWithdraw}
        consentData={consentData}
        providePhraseId=""
        withdrawMessageId="privacy.healthInfo.withdraw.button.label"
        onWithdrawConsent={onWithdrawConsent}
      />
    </PolicyStatusBasedOnPolicyName>
  );
};

const DependentAuthPolicyStatus = (props: any) => {
  const { consentData, onWithdrawConsent } = props;

  return (
    <PolicyStatusBasedOnPolicyName
      {...props}
      title={<FormattedMessage id="privacy.dependentAuthBlock.heading" />}
      name={PolicyName.DEPENDENT_AUTHORIZATION}
      showSummary={false}
      privacyLink={(
        <LegalDocumentLinks.PrivacyPolicy className="font-footnote font-color-profile-accent">
          <FormattedMessage id="privacy.seeFullPrivacyPolicy.button.label" />
        </LegalDocumentLinks.PrivacyPolicy>
      )}
    >
      <ConsentStatusMessage
        consentData={consentData}
        acceptedMessageId="privacy.dependentAuth.notUserProvided.message"
        withdrawMessageId=""
        notProvidedMessageId="privacy.informedConsent.notProvided.message"
      />

      <ConsentAction
        allowAction={true}
        consentData={consentData}
        providePhraseId=""
        withdrawMessageId="privacy.dependentAuth.withdraw.button.label"
        onWithdrawConsent={onWithdrawConsent}
      />
    </PolicyStatusBasedOnPolicyName>
  );
};

export {
  PolicyStatusBasedOnPolicyName,
  HealthInfoPolicyStatus,
  DependentAuthPolicyStatus,
};

export default PolicyStatus;
